import { useQuery, useQueryClient } from 'react-query';
import { CallGroupApi } from '@frontend/api-call-group';
import { queryKeys } from '../query-keys';

interface CallGroupExpansionData {
  deviceNames: string[];
  deviceCount: number;
  callGroupName?: string;
}

export const useCallGroupCacheUpdater = (callGroupId: string) => {
  const queryClient = useQueryClient();

  // Query to get which call routes use this call group
  const { data: usageData = [] } = useQuery({
    queryKey: queryKeys.callGroupUsageV2(callGroupId),
    queryFn: () => CallGroupApi.getCallGroupUsageV2({ callGroupId }),
    select: (data) => data?.usages ?? [],
    staleTime: 0,
    enabled: !!callGroupId,
  });

  const updateCallRouteCaches = (expansionData: CallGroupExpansionData) => {
    if (!usageData?.length) return;

    // Only update caches for call route usages, not override usages
    const callRouteUsages = usageData.filter((usage) => 'callRouteUsage' in usage);

    callRouteUsages.forEach(({ callRouteUsage: { callRouteId } }) => {
      const queryKey = ['call-route-v1', 'Read', { callRouteId }];

      // Cancel any outgoing queries for this call route
      queryClient.cancelQueries(queryKey);

      queryClient.setQueryData(queryKey, (oldCallRoute: any) => {
        if (!oldCallRoute) {
          return oldCallRoute;
        }

        // Update any expanded call group data in the call route
        const updateCallGroupExpansion = (obj: any): any => {
          if (!obj || typeof obj !== 'object') return obj;

          if (Array.isArray(obj)) {
            return obj.map(updateCallGroupExpansion);
          }

          // If this is a node with call group expansion matching our call group ID
          if (obj.callObject?.primitiveId === callGroupId && obj.callObject?.callGroupExpansion) {
            const updated = {
              ...obj,
              callObject: {
                ...obj.callObject,
                ...(expansionData.callGroupName && { primitiveName: expansionData.callGroupName }),
                callGroupExpansion: {
                  ...obj.callObject.callGroupExpansion,
                  deviceCount: expansionData.deviceCount,
                  deviceNames: expansionData.deviceNames,
                },
              },
            };
            return updated;
          }

          // Recursively update nested objects
          const updated = { ...obj };
          Object.keys(updated).forEach((key) => {
            updated[key] = updateCallGroupExpansion(updated[key]);
          });

          return updated;
        };

        return updateCallGroupExpansion(oldCallRoute);
      });

      // Force the query to be stale for a short period to prevent immediate refetch
      queryClient.setQueryDefaults(queryKey, { staleTime: 5000 });
    });
  };

  return {
    usageData: usageData || [],
    updateCallRouteCaches,
  };
};
